/**
 * The Laser system card: the laser and its hardware facts.
 *
 * The hardware facts (manufacturer, model, scan head, nominal power, marking
 * field, beam waist, marking file type) come with the laser's template and
 * cannot be changed per exploration: the server refuses a laser config whose
 * identity differs from the template, so they are shown, not edited.
 * Calibration repetitions are a field only when the template carries the
 * key (adding it is refused). The design space the optimizer searches is on
 * the Design space card.
 */
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ChoiceCard from "./ChoiceCard";
import SettingFields from "./SettingFields";

const IDENTITY_ROWS = [
  ["manufacturer", "Manufacturer"],
  ["laser_model_name", "Laser"],
  ["scanhead_model_name", "Scan head"],
  ["nominal_power", "Nominal power", (value) => `${value} W`],
  ["marking_area_size", "Marking field", (value) => (Array.isArray(value) ? `${value[0]} × ${value[1]} mm` : String(value))],
  ["beam_waist_radius", "Beam waist radius", (value) => `${value} µm`],
  ["marking_file_type", "Marking file"],
];

const REPETITIONS_FIELD = {
  path: ["calibration_repetitions"],
  label: "Calibration repetitions",
  kind: "integer",
  min: 1,
  help: "Times header, labels and crosses are traced",
};

export function laserIdentity(laserConfig) {
  return IDENTITY_ROWS.filter(([key]) => laserConfig?.[key] !== undefined && laserConfig?.[key] !== null).map(
    ([key, label, format]) => ({ key, label, value: format ? format(laserConfig[key]) : String(laserConfig[key]) })
  );
}

/** "MPI IPG YLPM, 20 W" for the card summary (empty without a template). */
export function laserSummary(laserConfig) {
  if (!laserConfig) return "";
  const parts = [[laserConfig.manufacturer, laserConfig.laser_model_name].filter(Boolean).join(" ")];
  if (laserConfig.nominal_power !== undefined && laserConfig.nominal_power !== null) parts.push(`${laserConfig.nominal_power} W`);
  return parts.filter(Boolean).join(", ");
}

export default function LaserCard({ rows, valueId, onLaserChange, laserConfig, onLaserConfigChange, onInvalidChange }) {
  const identity = laserIdentity(laserConfig);
  const hasRepetitions = laserConfig?.calibration_repetitions !== undefined;

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      <ChoiceCard
        inputId="exploration-laser"
        label="Laser system name"
        rows={rows}
        valueId={valueId}
        onChange={onLaserChange}
        manage={{ text: "New lasers are created and managed on the", linkLabel: "Lasers", route: "/lasers" }}
      />
      {identity.length > 0 ? (
        <Box
          data-testid="laser-identity"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#f6f8fb",
          }}
        >
          {identity.map((row) => (
            <Box key={row.key}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
      {hasRepetitions ? (
        <SettingFields
          config={laserConfig}
          fields={[REPETITIONS_FIELD]}
          onChange={onLaserConfigChange}
          onInvalidChange={onInvalidChange}
          testPrefix="laser"
        />
      ) : null}
      {identity.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          The parameters the optimizer searches are on the Design space card.
        </Typography>
      ) : null}
    </Stack>
  );
}
